import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import type {
  CreateWorkPlaceRequest,
  UpdateWorkPlaceRequest,
  WorkPlaceResponse
} from 'generated';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { planUnitsAtom } from '../../atoms';

import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {
  ControlledAutoComplete,
  ControlledDatePicker,
  ControlledTextField,
  Dialog
} from 'components/shared';

import addWorkPlaceSchema from './add-workplace-schema';
import dayjs from 'dayjs';
import { getPlanUnitIds } from '../../../../utils';
import { useGetCountryCodeOptions } from 'hooks/useGetCountryCodeOptions';
import {
  GridContainer,
  GridCompact12,
  GridCompact4,
  GridCompact8
} from 'components/shared/Grid/Grid.components';

const schema = addWorkPlaceSchema();
type Form = InferType<typeof schema>;

const defaultValues = {
  name: '',
  description: '',
  validFrom: dayjs().startOf('day'),
  validTo: dayjs().add(1, 'day'),
  planUnits: [],
  feature: '',
  streetName: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  countryCode: null
};

type Props = {
  open: boolean;
  handleClose: () => void;
  workPlace?: WorkPlaceResponse;
};

export default function AddWorkPlaceDialog({
  open,
  handleClose,
  workPlace
}: Props) {
  const { t } = useTranslation();
  const countryCodeOptions = useGetCountryCodeOptions();
  const queryClient = useQueryClient();
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);

  const { mutate: createWorkPlace, isLoading } = mutations.useCreateWorkPlace();
  const { mutate: updateWorkPlace } = mutations.useUpdateWorkPlace();

  const { control, handleSubmit, reset, setValue, trigger } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  useEffect(() => {
    if (!workPlace) {
      reset(defaultValues);
      return;
    }

    reset({
      name: workPlace.name,
      description: workPlace.description,
      validFrom: workPlace.validFrom,
      validTo: workPlace.validTo && dayjs(workPlace.validTo),
      planUnits: selectedPlanUnits,
      feature: workPlace.feature,
      streetName: workPlace.address?.streetName,
      houseNumber: workPlace.address?.houseNumber,
      postalCode: workPlace.address?.postalCode,
      city: workPlace.address?.city,
      countryCode:
        countryCodeOptions.find(
          ({ value }) =>
            value?.toString().toLowerCase() ===
            workPlace.address?.country?.toLowerCase()
        ) || null
    });
  }, [workPlace]);

  useEffect(() => {
    if (!selectedPlanUnits || selectedPlanUnits.length === 0)
      trigger('planUnits');
    setValue('planUnits', selectedPlanUnits, { shouldDirty: false });
  }, [selectedPlanUnits, workPlace]);

  async function onSubmit(data) {
    const {
      name,
      description,
      validFrom,
      validTo,
      feature,
      streetName,
      houseNumber,
      postalCode,
      city,
      countryCode
    } = data;

    if (workPlace) {
      const payload: UpdateWorkPlaceRequest = {
        entityId: workPlace.entityId,
        name,
        description,
        validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(validTo).format('YYYY-MM-DD'),
        feature,
        address: {
          streetName,
          houseNumber,
          postalCode,
          city,
          countryCode: countryCode?.value || ''
        }
      };

      await updateWorkPlace(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getWorkPlaces.name);
          queryClient.invalidateQueries('planning');
          handleClose();
        }
      });

      return;
    }

    const payload: CreateWorkPlaceRequest = {
      name,
      description,
      organizationalUnitId: getPlanUnitIds(selectedPlanUnits)[0],
      validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
      validTo: dayjs(validTo).format('YYYY-MM-DD'),
      feature,
      address: {
        streetName,
        houseNumber,
        postalCode,
        city,
        countryCode: countryCode?.value || ''
      }
    };

    createWorkPlace(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getWorkPlaces.name);
        queryClient.invalidateQueries('planning');
        handleClose();
      }
    });
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        title={
          workPlace
            ? t('activityPlanning.editWorkPlace')
            : t('activityPlanning.addWorkPlace')
        }
        open={open}
        handleClose={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <GridContainer>
            <GridCompact12>
              <Controller
                name="planUnits"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    label={t('planUnit')}
                    disabled
                    variant="outlined"
                    value={(field.value || [])
                      .map(({ name }) => name)
                      .join(', ')}
                    onChange={({ target: { value } }) => {
                      field.onChange(value);
                    }}
                    error={!!fieldState?.error?.type}
                    helperText={fieldState?.error?.message}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </GridCompact12>
            <GridCompact4>
              <ControlledTextField
                name="name"
                control={control}
                label={t('name')}
                variant="outlined"
              />
            </GridCompact4>
            <GridCompact8>
              <ControlledTextField
                name="description"
                control={control}
                label={t('description')}
                variant="outlined"
              />
            </GridCompact8>
            <GridCompact4>
              <ControlledDatePicker
                name="validFrom"
                control={control}
                label={t('validFrom')}
                variant="outlined"
              />
            </GridCompact4>
            <GridCompact4>
              <ControlledDatePicker
                name="validTo"
                control={control}
                label={t('validTo')}
                variant="outlined"
              />
            </GridCompact4>

            <GridCompact8>
              <ControlledTextField
                name="streetName"
                control={control}
                label={t('street')}
                variant="outlined"
              />
            </GridCompact8>
            <GridCompact4>
              <ControlledTextField
                name="houseNumber"
                control={control}
                label={t('houseNumber')}
                variant="outlined"
              />
            </GridCompact4>

            <GridCompact4>
              <ControlledTextField
                name="postalCode"
                control={control}
                label={t('postalCode')}
                variant="outlined"
              />
            </GridCompact4>
            <GridCompact8>
              <ControlledTextField
                name="city"
                control={control}
                label={t('city')}
                variant="outlined"
              />
            </GridCompact8>

            <GridCompact12>
              <ControlledAutoComplete
                name={'countryCode'}
                control={control}
                label={t('country')}
                variant="outlined"
                fullWidth
                idProperty="value"
                optionLabelProp="label"
                options={countryCodeOptions || []}
              />
            </GridCompact12>

            <GridCompact12>
              <ControlledTextField
                name="feature"
                control={control}
                label={t('feature')}
                variant="outlined"
              />
            </GridCompact12>
          </GridContainer>

          <DialogActions sx={{ mt: 3 }}>
            <Button
              type="button"
              variant="text"
              color="primary"
              onClick={() => {
                handleClose();
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
